import { z } from 'zod/v4';
import { RowKind } from '../../session/orchestration/tool-protocol/follow-up-refs.js';

// ════════════════════════════════════════════════════════════════════════════
// `Links` — the generic kind, and the one that makes attach total.
//
// U18: a pillar with no dedicated format falls back here, and this kind
// renders RICH LINK CARDS. So its payload may only hold what EVERY pillar's
// row record provably carries — nothing read out of a per-pillar projection,
// which the record types as `unknown` and which no generic code may guess at.
//
// GROUNDING (D28 — the population, named):
//
//   `title`   the label the writer put on the link: `[label](url)` (U14). Not
//             derived from the row at all — the model naming the row IS the
//             title, which is why this kind needs no per-pillar read to have
//             one. Null when the writer offered a bare URL.
//   `url`     the row's durable link: the CITATION link the writer attached
//             when the block named the row by one, otherwise the row record's
//             own `address.url` (typed on the shared record, captured at
//             projection time before the shortener rewrites anything).
//             Nullable because `address.url` is — rows of pillars that address
//             a record rather than a page carry none. An ACTION link can never
//             land here: neither source can produce one, which is §7.4's
//             "unrepresentable by construction" expressed in the type.
//   `host`    derived from `url`. Never a second stored fact.
//   `rowKind` `RowRefRecord.kind` — `RowKind`, typed on the shared record and
//             set by every mint.
//
// PER-PILLAR ON THIS PATH (R18). `snippet` and `thumbnailUrl` are the two
// rich-card halves of U18, and the SHARED row record carries neither — both
// live one level down, inside `payload`, which this kind sees as `unknown`.
// They are filled by the BL's per-kind typed narrows
// (apps/bl … attached-result/links-card-projectors.ts), one narrow per
// pillar shape with captured payload evidence, null on any mismatch and null
// wherever the censused captures hold no material — the census, with its
// populations and sources, heads that module. Nothing generic reads
// `payload`; a card with a title, a host and a link is the honest render for
// every unevidenced kind.
// ════════════════════════════════════════════════════════════════════════════

export const LINKS_PAYLOAD_VERSION = 1;

export const linksRowSchema = z.object({
  title: z.string().nullable(),
  url: z.string().nullable(),
  host: z.string().nullable(),
  rowKind: z.enum(RowKind).nullable(),
  snippet: z.string().nullable(),
  thumbnailUrl: z.string().nullable(),
});

export const linksPayloadSchema = z.object({
  rows: z.array(linksRowSchema).min(1),
});

export type LinksRow = z.infer<typeof linksRowSchema>;
export type LinksPayload = z.infer<typeof linksPayloadSchema>;
